import { useEffect } from 'react';
import {
  hasActiveSubscriptionToGoal,
  hasActiveIconicSubscriptionToGoal,
  hasActiveIconicProSubscriptionToGoal
} from '@models/Me';
import styled from '@emotion/styled';
import Dynamic from '@base/Dynamic/Dynamic';
import { emptyObject } from '@constants/empty';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { mediaQuery, Spacings } from '@styles/index';
import { APP_CONTAINER_WIDTH } from '@styles/constants/spacings';
import useSkuDetails from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/useSkuDetails';
import { isCbtProgramme } from '@cont/SeeAll/utils';
import { getShowSaveIcon } from './utils';
import ScheduleCard from './ScheduleCard';
import useInfiniteCards from './useInfiniteCards';

const Modals = Dynamic(() => import('./Modals'));

const OuterWrapper = styled.div`
  position: absolute;
  top: 235px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  ${mediaQuery.uptoMobile} {
    top: 160px;
  }
`;

const ScrollableWrapper = styled.div`
  height: calc(100vh - 235px);
  overflow: scroll;

  ${mediaQuery.uptoMobile} {
    height: calc(100vh - 160px);
  }
`;

const InnerWrapper = styled.div`
  margin: auto;
  width: ${APP_CONTAINER_WIDTH};
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_6B};
    margin-bottom: ${Spacings.SPACING_18B};
  }
`;

export const useCardsInfo = (cardInfo) => {
  const { goalUid, collectionUid } = cardInfo ?? emptyObject;

  const { myInfo } = useMyInfo();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { hasSkuAccess, mutate, refetchUam } = useSkuDetails(
    goalUid,
    collectionUid
  );

  useEffect(() => {
    if (isLoggedIn) {
      mutate();
      refetchUam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const isIconicUser = hasActiveIconicSubscriptionToGoal(myInfo ?? {}, goalUid);
  const isIconicProUser = hasActiveIconicProSubscriptionToGoal(
    myInfo ?? {},
    goalUid
  );
  const isPlusUser = hasActiveSubscriptionToGoal(myInfo ?? {}, goalUid);
  const isSubscribed = isIconicUser || isIconicProUser || isPlusUser;

  const showSaveIcon = getShowSaveIcon({
    isLoggedIn,
    hasSkuAccess,
    isSubscribed
  });

  return {
    showSaveIcon,
    hasSkuAccess,
    isSubscribed,
    collectionUid
  };
};

const CardsContainer = () => {
  const { cardsList, ref, programmeType } = useInfiniteCards();
  const isCbt = isCbtProgramme(programmeType);

  const { showSaveIcon, hasSkuAccess, isSubscribed, collectionUid } =
    useCardsInfo(cardsList?.[0]?.cards?.[0]);

  return (
    <>
      <OuterWrapper>
        <ScrollableWrapper>
          <InnerWrapper>
            {cardsList.map((cardsInfo, index) => {
              const isLastCard = cardsList.length - 1 === index;
              const key = `${cardsInfo.date}-${index}`;

              return (
                <ScheduleCard
                  key={key}
                  cardsInfo={cardsInfo}
                  showSaveIcon={showSaveIcon}
                  hasSkuAccess={hasSkuAccess}
                  isSubscribed={isSubscribed}
                  collectionUid={collectionUid}
                  isCbt={isCbt}
                  ref={isLastCard ? ref : null}
                />
              );
            })}
          </InnerWrapper>
        </ScrollableWrapper>
      </OuterWrapper>
      <Modals />
    </>
  );
};

export default CardsContainer;
